import classNames from 'classnames';
import { type JSX, useId, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import styles from './styles/styles.module.scss';

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=1-11&node-type=canvas&t=0LDa2qcWEfD8NDHF-0

type TooltipProps = {
  anchorElement: JSX.Element;
  anchorContainerClassName?: string;
  containerClassName?: string;
  maxWidth?: number;
  offset?: number;
  popperClassName?: string;
  hidePointer?: boolean;
  uniqueId?: string;
  text: string;
  textClassName?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'right' | 'left';
  anchorHandlesFocus?: boolean;
};

export default function Tooltip({
  anchorElement,
  anchorContainerClassName,
  containerClassName,
  maxWidth,
  offset,
  popperClassName,
  hidePointer = false,
  uniqueId,
  text,
  textClassName,
  tooltipPlacement,
  anchorHandlesFocus = false
}: TooltipProps) {
  const tooltipMaxWidth = maxWidth ? Math.min(maxWidth, 240) : 'none';
  const [isTooltipShown, setIsTooltipShown] = useState(false);
  const generatedId = useId();
  const id = uniqueId ?? generatedId;
  const tooltipId = `${id}-tooltip`;

  const tooltipContent = (
    <>
      {/* This allows the tooltip text to be visible to screenreaders and testing libraries when the tooltip isn't in the DOM: */}
      <div hidden id={isTooltipShown ? '' : tooltipId}>
        <span className={textClassName}>{text}</span>
      </div>
      <ReactTooltip
        id={anchorHandlesFocus ? id : tooltipId}
        delayShow={300}
        float={hidePointer}
        place={!maxWidth ? undefined : tooltipPlacement}
        globalCloseEvents={{ escape: true }}
        className={classNames(styles.popper, popperClassName)}
        classNameArrow={styles.arrow}
        style={{ maxWidth: tooltipMaxWidth }}
        noArrow={hidePointer}
        offset={offset}
        opacity={1}
        afterShow={() => setIsTooltipShown(true)}
        afterHide={() => setIsTooltipShown(false)}
      >
        <span className={textClassName} aria-hidden={anchorHandlesFocus}>
          {text}
        </span>
      </ReactTooltip>
    </>
  );

  if (anchorHandlesFocus) {
    return (
      <div {...setDenaliAttribute('Tooltip')} className={containerClassName}>
        {anchorElement}
        {tooltipContent}
      </div>
    );
  }

  return (
    <div {...setDenaliAttribute('Tooltip')} className={containerClassName}>
      <div
        data-tooltip-id={tooltipId}
        // Allow focusing
        tabIndex={0}
        aria-describedby={tooltipId}
        className={anchorContainerClassName}
      >
        {anchorElement}
      </div>
      {tooltipContent}
    </div>
  );
}
